import React, { useEffect, useMemo, useState } from "react";
import {
  useSubscriptionHistory,
  SubscriptionHistoryTable,
  SubscriptionPeriodCountCard,
  ExpirePremiumSection,
} from "../../components/SubscriptionHistory";
import SubscriptionEditModal from "../../components/SubscriptionHistory/SubscriptionEditModal";
import { FilterSection, ActiveFilterPills } from "../../components/UserProfiles";
import PaginationBar from "../../components/common/PaginationBar";
import { getAdminPanelRole } from "../../utils/adminPanelRole";
import adminService from "../../api/services/adminService";

const SUBSCRIPTION_PILL_CLASSES = {
  recordType: "bg-sky-100 text-sky-800",
  user_id: "bg-purple-100 text-purple-800",
  name: "bg-slate-100 text-slate-800",
  email: "bg-cyan-100 text-cyan-800",
  contactNo: "bg-orange-100 text-orange-800",
  date_from: "bg-blue-100 text-blue-800",
  date_to: "bg-green-100 text-green-800",
  subscription: "bg-indigo-100 text-indigo-800",
  source: "bg-amber-100 text-amber-800",
  record_state: "bg-emerald-100 text-emerald-800",
  currency: "bg-teal-100 text-teal-800",
  status: "bg-rose-100 text-rose-800",
  emailType: "bg-violet-100 text-violet-800",
  account_deleted: "bg-red-100 text-red-800",
};

function getSubscriptionPillClassName(key) {
  return SUBSCRIPTION_PILL_CLASSES[key] || "bg-gray-100 text-gray-800";
}

const PAGE_TABS = {
  records: "records",
  expire: "expire",
  period: "period",
};

const Subscription = () => {
  const {
    historyFilters,
    handleFilterChange,
    clearFilters,
    removeFilter,
    getFilterLabel,
    getActiveFiltersCount,
    activeFilterEntries,
    primaryFields,
    subscriptionHistory,
    currentPage,
    setCurrentPage,
    totalPages,
    totalItems,
    manualStats,
    revenueCatStats,
    loadingHistory,
    historyError,
    limit,
    fetchSubscriptionHistory,
  } = useSubscriptionHistory({ enabled: true });

  const isDeveloper = getAdminPanelRole() === "developer";
  const canExpirePremium = ["admin", "developer"].includes(getAdminPanelRole());
  const canDelete = isDeveloper;
  const canEdit = isDeveloper;
  const [editItem, setEditItem] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [pageTab, setPageTab] = useState(PAGE_TABS.records);

  const tabs = useMemo(() => {
    const list = [{ id: PAGE_TABS.records, label: "All Records" }];
    if (canExpirePremium) {
      list.push({ id: PAGE_TABS.expire, label: "Expire premium" });
    }
    list.push({ id: PAGE_TABS.period, label: "Period count" });
    return list;
  }, [canExpirePremium]);

  useEffect(() => {
    if (!isDeveloper) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await adminService.getSubscriptionPlans();
        if (!cancelled && Array.isArray(res?.plans)) setSubscriptionPlans(res.plans);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDeveloper]);

  useEffect(() => {
    if (!tabs.some((t) => t.id === pageTab) && tabs[0]) {
      setPageTab(tabs[0].id);
    }
  }, [tabs, pageTab]);

  return (
    <div className="p-4 sm:p-6 w-full min-w-0 overflow-x-hidden bg-gray-50/60 min-h-[calc(100vh-4rem)]">
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="px-5 sm:px-6 pt-4 sm:pt-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-wrap gap-2 mb-0">
            {tabs.map((tab) => {
              const active = pageTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setPageTab(tab.id)}
                  className={`px-4 py-2.5 rounded-t-lg text-sm font-semibold border-b-2 transition-colors ${
                    active
                      ? "text-red-600 border-red-500 bg-white"
                      : "text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-100/80"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {pageTab === PAGE_TABS.records ? (
          <>
            <div className="p-5 sm:p-6 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 tracking-tight">
                    All Records
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    All subscription records from userSubscriptions. Use filters to narrow results.
                  </p>
                </div>
                {getActiveFiltersCount() > 0 && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
            <div className="p-5 sm:p-6">
              <ActiveFilterPills
                activeKeys={activeFilterEntries}
                filters={historyFilters}
                getLabel={getFilterLabel}
                onRemove={removeFilter}
                label="Active Filters:"
                getPillClassName={getSubscriptionPillClassName}
              />
              <FilterSection
                primaryFields={primaryFields}
                secondaryFields={[]}
                values={historyFilters}
                onChange={(e) => handleFilterChange(e.target.name, e.target.value)}
                onApply={() => setCurrentPage(1)}
                onClear={clearFilters}
                applyLabel="Apply"
                clearLabel="Clear All"
              />
              <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3">
                <span className="text-sm font-medium text-gray-700">Manual subscriptions:</span>
                <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                  {manualStats.active} active
                </span>
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  {manualStats.expired} expired
                </span>
                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                  {manualStats.canceled} canceled
                </span>
              </div>
              <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3">
                <span className="text-sm font-medium text-gray-700">RevenueCat subscriptions:</span>
                <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                  {revenueCatStats.active} active
                </span>
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  {revenueCatStats.expired} expired
                </span>
                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                  {revenueCatStats.canceled} canceled
                </span>
              </div>
              <SubscriptionHistoryTable
                items={subscriptionHistory}
                isLoading={loadingHistory}
                error={historyError}
                onRetry={() => fetchSubscriptionHistory(currentPage, historyFilters)}
                canDelete={canDelete}
                canEdit={canEdit}
                canExpirePremium={canExpirePremium}
                onEditRow={setEditItem}
              />
              <SubscriptionEditModal
                open={Boolean(editItem)}
                item={editItem}
                plans={subscriptionPlans}
                onClose={() => setEditItem(null)}
                onSaved={() => {
                  setEditItem(null);
                  fetchSubscriptionHistory(currentPage, historyFilters);
                }}
              />
              <PaginationBar
                page={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={limit}
                label="subscription records"
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        ) : null}

        {pageTab === PAGE_TABS.expire && canExpirePremium ? (
          <div className="p-5 sm:p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-1">Expire user premium</h2>
            <ExpirePremiumSection
              embedded
              onSuccess={() => fetchSubscriptionHistory(currentPage, historyFilters)}
            />
          </div>
        ) : null}

        {pageTab === PAGE_TABS.period ? (
          <div className="p-5 sm:p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-1">Subscriptions in period</h2>
            <SubscriptionPeriodCountCard
              embedded
              onApplyToList={(from, to) => {
                handleFilterChange("date_from", from);
                handleFilterChange("date_to", to);
                setPageTab(PAGE_TABS.records);
                setCurrentPage(1);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Subscription;
